/**
 * What to call a fiscal register on screen — `BACKEND.md` §10A.35 step 3.
 *
 * The mirror of `channelLabel` for the other thing that has a name: a register. §10A.35 gave
 * `fiskal_settings` a `label` column and made `GET /fiskal/registers` answer it, so a register renamed
 * in the manager should be renamed everywhere. Its closing paragraph names the screens that still
 * write *Kasa Reštaurácia* as a literal and says why they matter — *"they are why a rename appears not
 * to take even where the row has changed"*.
 *
 * The row's own `label` wins. `fallbacks` covers the moment before the call returns, so a screen drawn
 * early still reads properly rather than flashing an id, and the id itself is the last resort: a tab
 * headed `terasa` is worse than a good name and far better than a register with no tab at all.
 */
export function registerName(
  kasaId: string | null | undefined,
  registers: readonly { kasaId?: string | null; label?: string | null }[],
  fallbacks: Readonly<Record<string, string>> = {},
): string {
  const wanted = (kasaId ?? '').trim()
  if (wanted === '') return ''
  const named = registers.find((register) => (register.kasaId ?? '').trim() === wanted)?.label?.trim()
  if (named !== undefined && named !== '') return named
  return fallbacks[wanted] ?? wanted
}

/**
 * The registers to draw, from what the endpoint answered — ids only, in its order.
 *
 * **Never empty.** A screen whose register list is empty offers nothing to click, which is worse than
 * offering the two every build of this port has shipped with; `fallback` is what it draws until the
 * call returns, and what it keeps if the call fails.
 */
export function registerIds(
  payload: unknown,
  fallback: readonly string[] = ['restauracia', 'rozvoz'],
): string[] {
  const rows = Array.isArray(payload) ? (payload as Array<{ kasaId?: unknown }>) : []
  const ids = rows
    .map((row) => (typeof row.kasaId === 'string' ? row.kasaId.trim() : ''))
    .filter((id) => id !== '')
  return ids.length > 0 ? ids : [...fallback]
}
